import { AggregationFunction, ConditionParams as ExportConditionParams } from "@multiweb/clients/dataReader";
import { ILongTransactionResult, IResultMessage } from "@multiweb/types/contracts";
import { IOWObjectList } from "./DataTypes/IOWObjectList";
import { RequestContext } from "./RequestContext";

export { ExportConditionParams }; // reexport, protože typ bude mít sice stejnou strukturu, ale časem se přesune do vlastního klienta

// ReSharper disable InconsistentNaming

export interface IExtendedObjectOperation {
    Operation: OWExtendedObjectOperationsType;
    Value: string;
}

export enum OWExtendedObjectOperationsType {
    SetAttributesAfterCreate,
    BeforeApplyUpdatesMethods,
    AfterCreateMethods,
    AfterApplyUpdatesMethods,
}

// TODO: zkontrolovat, zda se následujicí typy stále používají

/**
 * @internal
 */
export interface IGetDataRowsWithParamsResult {
    AttributesInfo: { Name: string; Type: string }[];
    IsVersioned: string;
    UsedGrouping: string;
    ClassName: string;
    Rows: [{
        IsEncrypted?: boolean;
        IsFinallyEncrypted?: boolean;
        Attrs?: IRowDataDTO;
    }];
}

/**
 * @internal
 */
export interface IRowDataDTO {
    ID: any; // hodnota atributu ID, která se vrátila z data readeru
    [name: string]: any;
}

export interface IOWObjectForTransfer {
    InstanceID: string;
    ClassName: string;
    ObjectIdentity: string;
    isEncrypted?: boolean;
    isEncryptionFinalized?: boolean;

    //CreateTime: string;
    //LastChangeTime: string;

    CreateAttrChangeCycle: number;
    AttrChangeCycle: number;

    RoleIDs: any[];
    EditIDs: any[];
    DeleteIDs: any[];
    CanEdit?: boolean;
    CanDelete?: boolean;
    CanExecuteActions?: boolean;

    isCopy?: boolean;
    isVersioned?: boolean;

    WorkflowID: number | null;

    AttrData: IAttrDataForTransfer[];
}

export interface IAttrDataForTransfer {
    Name: string;
    Type: string;

    FullObject?: boolean;
    OnlyChanges?: boolean;
    DataOnRequest?: boolean;
    IsEncrypted?: boolean;
    ClassName?: string;
    LastChange?: string;
    InstanceID?: string;

    Value: any;
}

/**
 * aplikační enum CZpusob_otevreni_frm - neměnit hodnoty
 */
export enum FormOpenType {
    form = 0,
    paramForm = 1,
    paramFormMethod = 2,
    paramFormReport = 3,
    paramConditionForm = 4,
}

export enum FormOpenMode {
    edit = 0,
    insert = 1,
}

/**
 * systémové operace
 */
export enum SystemOperation {
    EDITIMPPROFILE = "EDITIMPPROFILE",
}

export interface IOWObjectJsonPatchDTO {
    /**
    * Typ operace
    */
    op: "setValue" | "setEncryptedValue" | "run"; // až budeme používat TS 2.5, tak předělat na výčťák
    /**
    * Název atributu, nebo metody
    */
    name: string;
    /**
    * Hodnota atributu, nebo parametry metody
    */
    value?: any;
}

export interface ICreatedObjects {
    /**
     * Instance vytvořených objektů
     */
    objects: string[];
    /**
     * Případné chybové zprávy
     */
    messages: IResultMessage[];
}

/** Obsahuje výsledek operace mazání záznamů */
export interface IDeleteObjectsResult extends ILongTransactionResult {
    /** Zda bylo mazání puštěno přes dispečera */
    inDispatcher: boolean;
    /** Seznam nesmazaných záznamů */
    failedIds: string[];
    /** Chybové zprávy vzniklé při mazání */
    messages: IResultMessage[];
}

/**
 * Obsahuje výsledek operace aplikování hromadných změn
 * */
export interface IMultiEditUpdatesResult extends ILongTransactionResult {
    /** Případné chybové zprávy */
    messages: IResultMessage[];
}

/**
 * Data odpovědi na dotaz na implicitní formulář
 */
export interface IImplicitFormInfo {
    FormName: string;
    DetailAssocName?: string;
    PurposeType?: FormOpenType;
    MasterAttr?: string;
}

/**
 * Data jednoho sloupce seznam
 */
export interface IListFrameColumn {
    /** Název atributu, který má být ve sloupci zobrazen */
    attrName: string;
    /** Lokalizační klíč do hlavičky sloupce ve formátu XX-XXX */
    locKey: string;
    /** Parametry, které se dosadí do lokalizovaného textu názvu sloupce */
    localizationParams?: string[];
    /** Formát, v jakém má být hodnota zobrazena */
    displayFormat?: string;
    /** ASC/DESC řazení */
    orderBy?: string;
    /** Požadovaná výchozí šířka sloupce */
    width: number;
    /** Editovatelnost sloupce */
    isEditable?: boolean;
}

/**
 * Hodnota parametru který zahrne agregační funkce do tisku seznamu.
 * @see ITableExportParams.showAggregRowStr
 */
export const SHOW_AGGREGATION_ROW_STRING = "RequestedFunctions";

/**
 * Data potřebná pro vytvoření exportu
 * @includeToDoc
 */
export interface ITableExportParams {
    /** Typ vytvářené exportu (pdf, docx, ...) */
    exportType: string;
    /** Název master objektu */
    objectName: string;
    /** Titulek formuláře (již lokalizovaný) */
    formCaption?: string;
    /** Lokalizační klíč titulku formuláře */
    formCaptionLocKey?: string;
    /** Zda budou čteny verzovaná data */
    readVersioned: boolean;
    /** Omezující podmínka */
    condition: string;
    /** Argumenty podmínky */
    conditionParams: ExportConditionParams;
    /** Instance master objektu */
    masterInstanceID: string | undefined;
    /** Požadované řazení */
    orderBy: string[];
    /** Seznam sloupců, které mají být obsaženy v exportu */
    attributes: ITableExportAttrDesc[];
    /** Zobrazí agregační řádek v tisku seznamu, pokud je hodnota rovna {@link SHOW_AGGREGATION_ROW_STRING} .*/
    showAggregRowStr?: typeof SHOW_AGGREGATION_ROW_STRING;
}

/**
 * Informace o jednom sloupci v exportu
 * @includeToDoc
 */
export interface ITableExportAttrDesc {
    /** Zobrazovaný atribut */
    attributeName: string;
    /** Loklizační klíč sloupce */
    locKey: string;
    /** Parametry lokalizovaného textu */
    locParams: string[];
    /** Požadovaná šířka sloupce */
    width: number;
    /** Název agregační funkce v případě že nějaká byla zvolena */
    aggregFunction: AggregationFunction | undefined | null;
}
// ReSharper restore InconsistentNaming

/**
 * Informace o limitech pro nahrání souborů do DMS. Povolené přípony a velikosti souborů
 */
export interface IDMSFileLimits {
    /** Zda je povoleno nahrávat soubory, které nejsou uvedeny ve fileLimits. Defaultně false */
    allowDefaultExtensions: boolean;
    /** Limit velikosti pro soubory, které jsou povoleny a není pro ně určeno jinak. 0 = nejde nehrávat, -1 = neomezená velikost */
    defaultExtensionsLimit: number;
    /** Skupiny povolených přípon a jejich velikostí */
    fileLimits: IDMSFileLimitItem;
    /** Slovník obsahuje části souborů, podle kterých se dá poznat, zda má správnou příponu */
    extensionMasks: IDMSExtensionMasks;
    /** seznam BOM masek */
    boms: string[];
}

/**
 * Skupiny povolených přípon a jejich velikostí.
 * Např:
 *	".pdf;.woff": -1,
 *	".exe": 0,
 *	".docx;.doc": 200000
 *
 * 0 = nejde nahrávat, -1 = neomezená velikost
 */
export interface IDMSFileLimitItem {
    [extensions: string]: number;
}

/**
 * Slovník obsahuje části souborů, podle kterých se dá poznat, zda má správnou příponu
 * Např:
 *	".pdf": ["25504446"],
 *	".mpeg": ["FFE.", "FFF.", "000001B."]
 */
export interface IDMSExtensionMasks {
    [extension: string]: string[];
}

export type RunMethodBaseParams = {
    /** Kontext, v rámci kterého bude metoda vykonána */
    requestContext?: RequestContext;
    /** Název metody */
    methodName: string;
    /** id formuláře */
    formId?: string;
    /** Lokalizovaný název metody */
    userLocKey?: string;
};

type RunMethodParamsAttr = {
  /** Parametry */
  params?: any[];
};

export type RunMethodWithParamsBaseParams = RunMethodBaseParams & RunMethodParamsAttr;

export type RunStaticMethodParams = RunMethodBaseParams & {
    /** Název třídy */
    className: string;
};

export type RunStaticMethodWithParamsConfig = RunStaticMethodParams & RunMethodParamsAttr;

export type RunMethodParams = RunMethodBaseParams & {
    /** Seznam objektů nad kterými se má akce provést*/
    objectList: IOWObjectList;
    /** ID podminky viditelnost */
    visibleCondId?: string;
    /** ID podminky enabled*/
    enabledCondId?: string;
    /** Příznak jestli je podmínka viditelnosti negovaná */
    isEnabledCondNegative?: boolean;
    /** Příznak jestli je podmínka aktivnosti negovaná */
    isVisibleCondNegative?: boolean;
};

export type RunMethodForIdParams = RunStaticMethodParams & {
    /** ID objektu */
    id: string;
};

export type RunObjectMethodParams = RunMethodBaseParams & {
    /** Instance objektu */
    instanceID: string;
};

export type RunObjectMethodWithParamsParams = RunObjectMethodParams & RunMethodParamsAttr;
